import * as React from "react";
import { useMemo, useState } from "react";
import {
    Box,
    Container,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Pagination,
    Tooltip,
    Paper,
    Avatar,
    Button,
    Divider,
    useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PageBanner from "../orderStatus/components/pageBanner";
import Copyright from "../Copyright";
import TrackingOrderDetail from "../orderStatus/trackingOrderDetail";
import { getStatusString } from "../newOrder/components/helperFunctions/index.js";
import "../orderStatus/orderDetail.css";

const PAGE_SIZE = 8;

function trackingLink(trackNumber) {
    return `${window.location.origin}/trackNo/${trackNumber}`;
}

function statusColor(status) {
    return status >= 8 ? "success" : "warning";
}

export default function JobList({ jobs, reference }) {
    const isDesktop = useMediaQuery("(min-width:1024px)");
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);
    const [copiedTrackNumber, setCopiedTrackNumber] = useState(null);
    const [selected, setSelected] = useState(null);
    const [sortOrder, setSortOrder] = useState("desc"); // desc = newest first

    const markCopied = (trackNumber) => {
        setCopiedTrackNumber(trackNumber);
        setTimeout(() => setCopiedTrackNumber((current) => (current === trackNumber ? null : current)), 1500);
    };

    const legacyCopy = (text) => {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(textarea);
        return ok;
    };

    const copyLink = (e, trackNumber) => {
        e.preventDefault();
        e.stopPropagation();
        const link = trackingLink(trackNumber);
        if (navigator.clipboard?.writeText) {
            navigator.clipboard
                .writeText(link)
                .then(() => markCopied(trackNumber))
                .catch(() => {
                    if (legacyCopy(link)) markCopied(trackNumber);
                });
        } else if (legacyCopy(link)) {
            markCopied(trackNumber);
        }
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const matches = !q
            ? jobs
            : jobs.filter((job) =>
                  [job.TrackNumber, job.cCompanyName, job.sCompanyName, job.cCity, job.sCity, getStatusString(job.Status)]
                      .filter(Boolean)
                      .some((field) => String(field).toLowerCase().includes(q))
              );
        return [...matches].sort((a, b) => {
            const da = a.IN_DATE ? new Date(a.IN_DATE).getTime() : 0;
            const db = b.IN_DATE ? new Date(b.IN_DATE).getTime() : 0;
            return sortOrder === "asc" ? da - db : db - da;
        });
    }, [jobs, query, sortOrder]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const currentPage = Math.min(page, pageCount);
    const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const handleQueryChange = (e) => {
        setQuery(e.target.value);
        setPage(1);
    };

    const rowSx = (job) => ({
        borderRadius: 2,
        mb: 1,
        border: "1px solid",
        borderColor: isDesktop && selected === job.TrackNumber ? "primary.main" : "divider",
        bgcolor: isDesktop && selected === job.TrackNumber ? "action.selected" : "background.paper",
        transition: "border-color .15s, background-color .15s",
        "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
    });

    const listHeader = (
        <Stack spacing={2} sx={{ p: { xs: 2, sm: 2.5 }, pb: 0 }}>
            <Stack spacing={0.5}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {jobs.length} deliveries found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    for reference <strong>{reference}</strong>
                    {jobs.length > PAGE_SIZE ? " — search below to narrow it down." : isDesktop ? ". Select one to view." : ". Select yours below."}
                </Typography>
            </Stack>

            {jobs.length > PAGE_SIZE && (
                <TextField
                    placeholder="Search by tracking #, company, city, status…"
                    size="small"
                    fullWidth
                    value={query}
                    onChange={handleQueryChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                            </InputAdornment>
                        ),
                        endAdornment: query && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => handleQueryChange({ target: { value: "" } })}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            )}
        </Stack>
    );

    const listRows = (
        <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2 }}>
            {jobs.length > 1 && (
                <Stack direction="row" justifyContent="flex-start" alignItems="center" sx={{ mb: 1 }}>
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                            setSortOrder((o) => (o === "desc" ? "asc" : "desc"));
                            setPage(1);
                        }}
                        endIcon={sortOrder === "desc" ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                        sx={{
                            color: "text.disabled",
                            "&:hover": { color: "primary.main", bgcolor: "transparent" },
                        }}
                    >
                        Date
                    </Button>
                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                </Stack>
            )}
            {filtered.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                    No deliveries match "{query}".
                </Typography>
            ) : (
                <List disablePadding>
                    {pageItems.map((job) => {
                        const hasTrackNumber = !!job.TrackNumber;
                        return (
                        <ListItemButton
                            key={job.jobID}
                            component={hasTrackNumber ? "a" : "div"}
                            href={hasTrackNumber ? trackingLink(job.TrackNumber) : undefined}
                            target={hasTrackNumber && !isDesktop ? "_blank" : undefined}
                            rel={hasTrackNumber && !isDesktop ? "noopener noreferrer" : undefined}
                            disabled={!hasTrackNumber}
                            onClick={(e) => {
                                if (hasTrackNumber && isDesktop) {
                                    e.preventDefault();
                                    setSelected(job.TrackNumber);
                                }
                            }}
                            sx={rowSx(job)}
                        >
                            <Avatar
                                variant="rounded"
                                sx={{
                                    mr: 2,
                                    width: 36,
                                    height: 36,
                                    bgcolor: job.Status >= 8 ? "success.light" : "primary.light",
                                    color: "#fff",
                                }}
                            >
                                <LocalShippingIcon fontSize="small" />
                            </Avatar>
                            <ListItemText
                                primary={hasTrackNumber ? job.TrackNumber : "No tracking number available"}
                                primaryTypographyProps={{ fontWeight: 600, fontStyle: hasTrackNumber ? "normal" : "italic" }}
                                secondary={[
                                    job.cCompanyName || job.cCity,
                                    job.IN_DATE ? String(job.IN_DATE).substring(0, 10) : null,
                                ]
                                    .filter(Boolean)
                                    .join(" — ")}
                            />
                            <Chip
                                size="small"
                                label={getStatusString(job.Status)}
                                color={statusColor(job.Status)}
                                variant="outlined"
                                sx={{ mr: 1 }}
                            />
                            {hasTrackNumber && (
                                <Tooltip title={copiedTrackNumber === job.TrackNumber ? "Copied!" : "Copy tracking link"}>
                                    <IconButton size="small" onClick={(e) => copyLink(e, job.TrackNumber)}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {hasTrackNumber && isDesktop && (
                                <Tooltip title="Open in new tab">
                                    <IconButton
                                        size="small"
                                        component="a"
                                        href={trackingLink(job.TrackNumber)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {hasTrackNumber && !isDesktop && (
                                <OpenInNewIcon fontSize="small" sx={{ ml: 0.5, color: "text.disabled" }} />
                            )}
                        </ListItemButton>
                        );
                    })}
                </List>
            )}
        </Box>
    );

    const listFooter = pageCount > 1 && (
        <Stack alignItems="center" sx={{ p: { xs: 2, sm: 2.5 }, pt: 1 }}>
            <Pagination
                count={pageCount}
                page={currentPage}
                onChange={(_, p) => setPage(p)}
                size="small"
                color="primary"
            />
        </Stack>
    );

    const showSplit = isDesktop && !!selected;
    const transition = "width 380ms cubic-bezier(.4,0,.2,1), margin 380ms cubic-bezier(.4,0,.2,1), max-width 380ms cubic-bezier(.4,0,.2,1)";

    return (
        <Box className="NewOrder" sx={{ overflow: "visible" }}>
            <PageBanner title="PARCEL STATUS" subheading="Select Your Delivery" />
            <Container maxWidth={isDesktop ? "xl" : "sm"}>
                <div id="pageContent" style={{ marginTop: "-48px" }}>
                    {isDesktop ? (
                        <Stack direction="row" spacing={showSplit ? 3 : 0} alignItems="flex-start">
                            <Paper
                                elevation={2}
                                sx={{
                                    borderRadius: 3,
                                    width: showSplit ? 440 : "100%",
                                    maxWidth: showSplit ? 440 : 600,
                                    mx: showSplit ? 0 : "auto",
                                    flexShrink: 0,
                                    position: "sticky",
                                    top: 80,
                                    maxHeight: "calc(100vh - 96px)",
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                    transition,
                                }}
                            >
                                {listHeader}
                                <Box sx={{ overflowY: "auto", flex: 1, minHeight: 0 }}>{listRows}</Box>
                                {listFooter}
                            </Paper>
                            {selected && (
                                <Box
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        "@keyframes detailFadeIn": {
                                            from: { opacity: 0, transform: "translateX(16px)" },
                                            to: { opacity: 1, transform: "translateX(0)" },
                                        },
                                        animation: "detailFadeIn 380ms cubic-bezier(.4,0,.2,1)",
                                    }}
                                    // Cancels out the first section card's own margin-top (.panel in
                                    // whitePanel.css, 10-15px depending on breakpoint) so it lines up
                                    // with the list panel's top edge.
                                    style={{ marginTop: -12 }}
                                >
                                    <TrackingOrderDetail key={selected} trackingNumberOverride={selected} embedded trakingType="trackingNumber" />
                                </Box>
                            )}
                        </Stack>
                    ) : (
                        <Paper elevation={2} sx={{ borderRadius: 3 }}>
                            {listHeader}
                            {listRows}
                            {listFooter}
                        </Paper>
                    )}
                </div>
            </Container>
            <footer>
                <Copyright />
            </footer>
        </Box>
    );
}
